import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { teamStats, REASON_OPTIONS, reasonLabel } from "@/lib/training";
import { positionAbbrev, positionColor } from "@/lib/playerConstants";
import { ArrowLeft, Trophy, TrendingDown, Users, Flame, BarChart3 } from "lucide-react";
import { toast } from "sonner";

export default function TrainingSeason() {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, t] = await Promise.all([api.get("/players"), api.get("/trainings")]);
        setPlayers(p.data);
        setTrainings(t.data);
      } catch {
        toast.error("Daten konnten nicht geladen werden");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const stats = useMemo(() => teamStats(players, trainings), [players, trainings]);

  if (loading) return <div className="text-center py-20 text-zinc-500 tracking-wide">Lädt…</div>;

  const topReasons = Object.entries(stats.reasonTotals)
    .sort((a, b) => b[1] - a[1])
    .filter(([, v]) => v > 0)
    .slice(0, 5);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <button
        onClick={() => navigate("/training")}
        className="text-xs text-zinc-400 hover:text-brand-lighter transition flex items-center gap-1 mb-5"
        data-testid="season-back-btn"
      >
        <ArrowLeft className="h-3 w-3" /> Zurück
      </button>

      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-wider text-brand-lighter mb-1">Auswertung</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-100">Saisonübersicht</h1>
        <p className="text-zinc-400 text-sm mt-1">Alle Trainings, Anwesenheit und Rankings auf einen Blick.</p>
      </div>

      {/* Team summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <Stat label="Trainings" value={stats.totalTrainings} testId="s-trainings" />
        <Stat label="Mannschaft" value={stats.artCounts.mannschaftstraining} testId="s-mannschaft" />
        <Stat label="Bonus" value={stats.artCounts.bonustraining} testId="s-bonus" />
        <Stat label="Anwesend" value={stats.totalAnw} color="text-emerald-400" testId="s-anw" />
        <Stat label="Entschuldigt" value={stats.totalEnt} color="text-amber-400" testId="s-ent" />
        <Stat label="Unentschuldigt" value={stats.totalUnent} color="text-rose-400" testId="s-unent" />
      </div>

      {/* Highlights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
        <HighlightCard
          icon={Users}
          label="Ø Team-Quote"
          value={`${stats.avgQuote.toFixed(1)} %`}
          sub="über alle Spieler"
          accent="text-brand-lighter"
          testId="s-avg-quote"
        />
        <HighlightCard
          icon={Trophy}
          label="Beste Quote"
          value={stats.best?.player.name || "—"}
          sub={stats.best ? `${stats.best.stats.quote.toFixed(1)} %` : "Keine Daten"}
          accent="text-emerald-400"
          testId="s-best"
        />
        <HighlightCard
          icon={TrendingDown}
          label="Schwächste Quote"
          value={stats.worst?.player.name || "—"}
          sub={stats.worst ? `${stats.worst.stats.quote.toFixed(1)} %` : "Keine Daten"}
          accent="text-rose-400"
          testId="s-worst"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div className="lg:col-span-2 rounded-lg border border-zinc-800 bg-zinc-900/40 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-wider text-zinc-400">Anwesenheits-Ranking</div>
            <BarChart3 className="h-4 w-4 text-zinc-500" />
          </div>
          {stats.ranking.length === 0 ? (
            <div className="text-sm text-zinc-500">Noch keine Spielerdaten.</div>
          ) : (
            <div className="space-y-1.5" data-testid="ranking-list">
              {stats.ranking.map((row, i) => (
                <button
                  key={row.player.id}
                  onClick={() => navigate(`/spieler/${row.player.id}`)}
                  className="w-full grid grid-cols-[32px_1fr_60px_60px] items-center gap-3 px-3 py-2 rounded-md hover:bg-zinc-800/40 text-left transition"
                  data-testid={`ranking-${i}`}
                >
                  <span className="font-display text-sm font-bold text-zinc-400 tabular-nums">{i + 1}.</span>
                  <div className="min-w-0">
                    <div className="text-zinc-100 font-medium truncate">{row.player.name}</div>
                    <div className={"text-[11px] tracking-wide font-medium " + positionColor(row.player.position)}>
                      {positionAbbrev(row.player.position)} · {row.player.jahrgang}
                    </div>
                  </div>
                  <span className="text-xs text-zinc-400 text-right tabular-nums">{row.stats.total}</span>
                  <span
                    className={
                      "font-display text-base font-bold text-right tabular-nums " +
                      (row.stats.quote >= 80 ? "text-emerald-400" : row.stats.quote >= 50 ? "text-amber-400" : "text-rose-400")
                    }
                    data-testid={`ranking-quote-${i}`}
                  >
                    {row.stats.quote.toFixed(1)}%
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] uppercase tracking-wider text-zinc-400">Längste Serie</div>
              <Flame className="h-4 w-4 text-amber-400" />
            </div>
            <div className="font-display text-3xl font-bold text-brand-lighter tabular-nums" data-testid="s-longest-streak">
              {stats.longestStreakOverall.streak}
            </div>
            <div className="text-xs text-zinc-400 mt-0.5">{stats.longestStreakOverall.name}</div>
          </div>

          <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5">
            <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-3">Häufigste Fehlgründe</div>
            {topReasons.length === 0 ? (
              <div className="text-sm text-zinc-500">Keine Entschuldigungen erfasst.</div>
            ) : (
              <div className="space-y-2">
                {topReasons.map(([key, count]) => (
                  <div key={key} className="flex items-center justify-between text-sm" data-testid={`reason-${key}`}>
                    <span className="text-zinc-300">{reasonLabel(key)}</span>
                    <span className="font-display text-base font-bold text-amber-400 tabular-nums">{count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color = "text-zinc-100", testId }) {
  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-3">
      <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div className={"font-display text-xl font-bold tabular-nums " + color} data-testid={testId}>{value}</div>
    </div>
  );
}

function HighlightCard({ icon: Icon, label, value, sub, accent, testId }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wider text-zinc-400">{label}</div>
        <Icon className={"h-4 w-4 " + accent} />
      </div>
      <div className="font-display text-2xl font-bold text-zinc-100 truncate">{value}</div>
      <div className={"text-xs mt-0.5 " + accent}>{sub}</div>
    </div>
  );
}
